import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import type EventBus from "../services/eventBus";
import RepositoryBase from "./repositoryBase";
import type DeadLetterTopicMessageResponse from "../entities/deadLetterTopicMessageResponse";
import DeadLetterTopicMessageActionResponse from "../entities/deadLetterTopicMessageActionResponse";
import type DeadLetterTopicMessagesSearchRequest from "../entities/deadLetterTopicMessagesSearchRequest";
import type DeadLetterTopicMessagesDiscardRequest from "../entities/deadLetterTopicMessagesDiscardRequest";
import type DeadLetterTopicMessagesResubmissionRequest from "../entities/deadLetterTopicMessagesResubmissionRequest";
import type DeadLetterTopicMessageActionsSearchRequest from "../entities/deadLetterTopicMessageActionsSearchRequest";
import DeadLetterTopicMessageDetailResponse from "../entities/deadLetterTopicMessageDetailResponse";
import DeadLetterTopicMessage from "../entities/deadLetterTopicMessageResponse";

export default class DeadLetterTopicsRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(pagination: IPagination, filter: DeadLetterTopicMessagesSearchRequest) {
    return this.callApi(api =>
      api
        .all("system")
        .all("dead_letter_topic_message_search")
        .postPagedEntities<DeadLetterTopicMessageResponse>({ ...filter, ...pagination }, DeadLetterTopicMessage)
    );
  }

  getAllActions(pagination: IPagination, filter: DeadLetterTopicMessageActionsSearchRequest) {
    return this.callApi(api =>
      api
        .all("system")
        .all("dead_letter_topic_message_action_search")
        .postPagedEntities<DeadLetterTopicMessageActionResponse>(
          { ...filter, ...pagination },
          DeadLetterTopicMessageActionResponse
        )
    );
  }

  get(dltMessageId: string) {
    return this.callApi(api =>
      api
        .all("system")
        .one("dead_letter_topic_messages", dltMessageId)
        .getEntity<DeadLetterTopicMessageDetailResponse>(DeadLetterTopicMessageDetailResponse)
    );
  }

  discardAll(request: DeadLetterTopicMessagesDiscardRequest) {
    return this.callApi(api => api.all("system").all("dead_letter_topic_messages").all("discards").postOnly(request));
  }

  resubmissionAll(request: DeadLetterTopicMessagesResubmissionRequest) {
    return this.callApi(api => api.all("system").all("dead_letter_topic_messages").all("resubmissions").postOnly(request));
  }

  update(dltMessage: DeadLetterTopicMessageDetailResponse) {
    return this.callApi(
      api =>
        api
          .all("system")
          .one("dead_letter_topic_message_search", dltMessage.id)
          .putEntity(dltMessage, DeadLetterTopicMessageDetailResponse),
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }),
      }
    );
  }
}
